import React, { useMemo, useRef } from "react"
import { Backdrop, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, OutlinedInput, Snackbar, TextField, Typography } from "@mui/material"
import { AgGridReact } from "ag-grid-react"
import { useCallback, useEffect, useState } from "react"
import UseColumnDefs from "../columns"
import { Rows } from "../../../../manager/carto"
import { Cached as ReloadIcon, Close as CloseIcon } from "@mui/icons-material"
import _ from "ansuko"
import "ansuko/plugins/prototype"
import UseApiManager from "../../../../manager/api"
import LOCALE_JA from "../../../../resources/aggrid/locale.ja"
import { useDialog } from "../../../../component/dialog"
import PropTypes from "prop-types"
import HeaderView from "./header"
import FooterView from "./footer"
import AddUserDialog from "./addUser"
import { eve, useEveListen } from "react-eve-hook"
import { USER_GRID_CHANGED, USER_LIST_IMPORTED, USER_UPDATE_FAILURE, USER_UPDATE_SUCCESS } from "../../../../Constraints"
import ExcelJS from "exceljs"

const styles = {
    root: {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    message: {
        text: {
            fontWeight: 'bold',
            fontSize: '12px',
            marginRight: '1rem'
        },
    },
    footer: {
        box: {
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            background: '#eee',
        }
    },
    loading: {
        box: {
            position: 'absolute',
            inset: '0px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#eeeeee6b',
        },
    }
}

const UserListView = ({ onClose }) => {

    const [rowData, setRowData] = useState()
    const [changePasswordUser, setChangePasswordUser] = useState()
    const [newPassword, setNewPassword] = useState("")
    const [reNewPassword, setReNewPassword] = useState("")
    const { PutFirst, Post, Put, Delete } = UseApiManager()
    const apiRef = useRef()
    const [successMessage, setSuccessMessage] = useState()
    const [failureMessage, setFailureMessage] = useState()
    const [deleteUser, setDeleteUser] = useState()
    const [loading, setLoading] = useState(false)
    const [openAddUserDialog, setOpenAddUserDialog] = useState(false)
    const originalDataRef = useRef()
    const [edited, setEdited] = useState()
    const isUpdatingInternallyRef = useRef(false)
    const areas = useMemo(() =>
        _.uniq(rowData?.map(row => row.area).filter(Boolean) ?? []).sort()
        , [rowData])
    const emails = useMemo(() => 
        rowData?.map(row => row.email).filter(Boolean).sort() ?? []
    , [rowData])
    const { openConfirm } = useDialog()

    const areaEditorParams = useCallback(params => {
        const values = new Set()
        params.api.forEachNode(node => {
            if (node.data?.area) values.add(node.data.area)
        })
        return { values: ["", ...values].sort((v1, v2) => v1.localeCompare(v2, "ja", { sensitivity: "base" })) }
    }, [])
    const { columnDefs } = UseColumnDefs({ areaEditorParams })

    const loadData = useCallback(() => {
        console.log("[LoadData]", isUpdatingInternallyRef.current)
        if (isUpdatingInternallyRef.current) { return }
        setRowData(null)
        Rows(`
            SELECT 
                cartodb_id,
                fb_uid,
                name,
                CASE WHEN area = '' THEN NULL ELSE area END AS area,
                temp_email as email
            FROM tokyo_users
        `).then(rows => {
            if (isUpdatingInternallyRef.current) return
            setRowData(rows)
            originalDataRef.current = _.cloneDeep(rows)
            _.waited(() => {
                eve(USER_GRID_CHANGED, {
                    currentRows: rows,
                    originalRows: rows,
                })
            })
        })
    }, [])

    const onGridReady = useCallback(params => {
        apiRef.current = params.api
    })

    const onAddUser = useCallback(() => {
        setOpenAddUserDialog(true)
    }, [])

    const onChangePassword = useCallback((data) => {
        setChangePasswordUser(data.node.data)
    }, [])

    const onChangePasswordSubmit = useCallback(() => {
        if (!changePasswordUser?.fb_uid || !newPassword) { return }
        PutFirst("user", {
            fb_uid: changePasswordUser.fb_uid,
            password: newPassword,
        }).then(() => {
            setChangePasswordUser(null)
            eve(USER_UPDATE_SUCCESS)
            _.waited(() => loadData())
        }).catch(e => {
            setChangePasswordUser(null)
            eve(USER_UPDATE_FAILURE)
            _.waited(() => loadData())
        })

    }, [changePasswordUser, newPassword, loadData])

    const onCellEditingStopped = useCallback((params) => {
        const rows = []
        params.api.forEachNode(node => {
            if (node.data.cartodb_id || node.data.name || node.data.email || node.data.temp_email) {
                rows.push(node.data)
            }
        })
        console.log("[User]", "cell editing stopped", params, rows)
        setRowData(rows)

        _.waited(() => {
            eve(USER_GRID_CHANGED, {
                currentRows: rows,
                originalRows: originalDataRef.current,
            })
        }, 2)
    }, [])

    const onDeleteUser = useCallback((data) => {
        setDeleteUser(data.node.data)
    }, [])

    const onSubmitAddUser = useCallback(data => {
        let rows
        setRowData(prev => {
            rows = [...prev, data]
            return rows
        })
        _.waited(() => {
            eve(USER_GRID_CHANGED, {
                currentRows: rows,
                originalRows: originalDataRef.current,
            })
        }, 3)
    })

    const onDownload = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('ユーザ一覧');

        sheet.columns = [
            { header: "", key: "cartodb_id", hide: true },
            { header: "名前", key: "name" },
            { header: "メールアドレス", key: "email" },
            { header: "エリア", key: "area" },
            { header: "新しいパスワード" },
            { header: "削除" },
        ]
        sheet.addRows(rowData)
        sheet.getColumn("A").hidden = true

        const uint8Array = await workbook.xlsx.writeBuffer()
        const blob = new Blob([uint8Array], { type: "application/octet-binary" })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = "ユーザ一覧.xlsx"
        a.click()
        a.remove()
    }, [rowData, columnDefs])

    const onImport = useCallback(() => {

    }, [])

    const controlCellRenderer = useCallback((data) => {
        return (
            <>
                <Button size="small" onClick={() => onChangePassword(data)}>パスワード変更</Button>
                {data.node.data?.cartodb_id && <Button size="small" color="secondary" onClick={() => setDeleteUser(data.node.data)}>削除</Button>}
            </>
        )
    }, [])

    const defaultColDef = useMemo(() => ({
        floatingFilter: true,
        editable: true,
    }), [])

    const onUpdate = useCallback(edit => {
        setLoading(true)
        Promise.all([
            _.isEmpty(edit.add) ? null :  Post("user/bulk", { users: edit.add }),
            _.isEmpty(edit.edit) ? null : Put("user/bulk", { users: edit.edit }),
            _.isEmpty(edit.delete) ? null : Delete("user/bulk", { users: edit.delete }),            
        ].filter(Boolean))
            .then(() => {
                eve(USER_UPDATE_SUCCESS)
                _.waited(() => {
                    loadData()
                })
            })
            .catch(e => {
                eve(USER_UPDATE_FAILURE)
            })
            .finally(() => setLoading(false))
    }, [])

    const onReset = useCallback(() => {
        console.log("[User]", "on reset")
        setRowData(_.cloneDeep(originalDataRef.current))
        eve(USER_GRID_CHANGED, {
            currentRows: originalDataRef.current,
            originalRows: originalDataRef.current,
        })
    }, [])

    const onDeleteSubmit = useCallback(() => {
        const d = { ...deleteUser }
        setDeleteUser(null)
        setLoading(true)
        Delete("user", d)
            .then(() => {
                eve(USER_UPDATE_SUCCESS)
                _.waited(() => {
                    loadData()
                }, 3)
            })
            .catch(e => {
                eve(USER_UPDATE_FAILURE)
                _.waited(() => {
                    loadData()
                }, 3)
            })
            .finally(() => setLoading(false))
    }, [deleteUser])

    useEffect(() => {
        loadData()
    }, [loadData])

    useEffect(() => {
        if (!successMessage) { return }
        const tm = setTimeout(() => setSuccessMessage(null), 3000)

        return () => {
            clearTimeout(tm)
        }
    }, [successMessage])

    useEffect(() => {
        if (!failureMessage) { return }
        const tm = setTimeout(() => setFailureMessage(null), 3000)

        return () => {
            clearTimeout(tm)
        }
    }, [failureMessage])

    useEveListen(USER_LIST_IMPORTED, rows => {
        console.log("[UserImported]", "received", rows)
        isUpdatingInternallyRef.current = true
        const newRows = []
        for (const row of rows) {
//            console.log(row)
            if (row.newPassword) {
                row.password = row.newPassword
            }
            delete row.newPassword
            if (!row.cartodb_id) {
                newRows.push(row)
            } else if (row.shouldDelete) {
                continue
            } else {
                const d = rowData?.find(r => r.cartodb_id === row.cartodb_id)
                if (!d) {
                    delete row.cartodb_id
                    newRows.push(row)
                } else {
                    newRows.push(row)
                }
            }
        }
        console.log("[UserList]", "変更後", newRows)
        setRowData([...newRows])
        _.waited(() => {
            isUpdatingInternallyRef.current = false
            console.log(originalDataRef.current)
            eve(USER_GRID_CHANGED, {
                currentRows: _.cloneDeep(newRows),
                originalRows: originalDataRef.current,
            })
        }, 3)
    }, [rowData])

    return (
        <Box style={styles.root}>
            <HeaderView
                onAddUser={onAddUser}
                onImport={onImport}
                onDownload={onDownload}
                onClose={onClose}
                onReload={loadData}
            />
            <AgGridReact
                style={{ flexGrow: 1 }}
                onGridReady={onGridReady}
                onCellEditingStopped={onCellEditingStopped}
                className="ag-theme-balham"
                columnDefs={columnDefs}
                localeText={LOCALE_JA}
                pagination={true}
                rowData={rowData}
                defaultColDef={defaultColDef}
                frameworkComponents={{
                    controlCellRenderer,
                }}
            />
            <FooterView
                onUpdate={onUpdate}
                onReset={onReset}
            />
            {!!changePasswordUser && (
                <Box
                    style={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        zIndex: 1, background: "#33333380",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <Box style={{
                        background: "white",
                        padding: "1rem",
                        borderRadius: "8px",
                        boxShadow: "1px 1px 8px #333",
                        width: "400px",
                    }}>
                        <Typography variant="h6">パスワードの変更</Typography>
                        <Box style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <Typography variant="caption">ユーザ「{changePasswordUser?.name ?? changePasswordUser?.email}」のパスワードを変更します</Typography>
                            <TextField variant="standard" type="password" label="新しいパスワード" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                            <TextField variant="standard" type="password" label="再入力" value={reNewPassword} onChange={e => setReNewPassword(e.target.value)} />
                        </Box>
                        <Box style={{ display: "flex", justifyContent: "end" }}>
                            <Button onClick={() => {
                                setChangePasswordUser(null)
                                _.waited(() => {
                                    setNewPassword("")
                                    setReNewPassword("")
                                })
                            }}>キャンセル</Button>
                            <Button
                                onClick={onChangePasswordSubmit}
                                disabled={!newPassword || newPassword !== reNewPassword}
                            >パスワード変更</Button>
                        </Box>
                    </Box>
                </Box>
            )}
            <AddUserDialog
                open={openAddUserDialog}
                areas={areas}
                onCancel={() => setOpenAddUserDialog(false)}
                onUpdated={onUpdate}
            />
            <Dialog open={!!deleteUser} style={{zIndex: 10001}}>
                <DialogTitle>ユーザの削除</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        [{deleteUser?.name ?? deleteUser?.temp_email}]を本当に削除しますか<br />この操作は取り消しできません
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteUser(null)}>キャンセル</Button>
                    <Button onClick={onDeleteSubmit} color="secondary">削除</Button>
                </DialogActions>
            </Dialog>
            {loading && (
                <Box style={styles.loading.box}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    )
}
UserListView.propTypes = {
    onClose: PropTypes.func,
}

export default UserListView